import { createAsyncTypes } from 'store/utils';
import { Configuration } from 'store/configurations';
import { ClientIntegration } from 'store/clientIntegrations';
import { KioskHardware } from 'store/kioskHardware';

// State Key
export const KEY = 'kiosks';

// Action Types
export const types = {
  GET_KIOSKS: createAsyncTypes(`${KEY}/GET_KIOSKS`),
  GET_KIOSK: createAsyncTypes(`${KEY}/GET_KIOSK`),
  CREATE_KIOSK: createAsyncTypes(`${KEY}/CREATE_KIOSK`),
  COPY_KIOSK: createAsyncTypes(`${KEY}/COPY_KIOSK`),
  UPDATE_KIOSK: createAsyncTypes(`${KEY}/UPDATE_KIOSK`),
  UPDATE_KIOSKS: createAsyncTypes(`${KEY}/UPDATE_KIOSKS`),
  DELETE_KIOSK: createAsyncTypes(`${KEY}/DELETE_KIOSK`),
  GET_KIOSK_SCHEDULE: createAsyncTypes(`${KEY}/GET_KIOSK_SCHEDULE`),
  UPDATE_KIOSK_SCHEDULE: createAsyncTypes(`${KEY}/UPDATE_KIOSK_SCHEDULE`),
  REBOOT_KIOSK: createAsyncTypes(`${KEY}/REBOOT_KIOSK`),
  CHANGE_STATUS_KIOSK: createAsyncTypes(`${KEY}/CHANGE_STATUS_KIOSK`),
  CHECK_APP_VERSION: createAsyncTypes(`${KEY}/CHECK_APP_VERSION`),
  LOG_EVENT: createAsyncTypes(`${KEY}/LOG_EVENT`),
  KIOSK_LOGIN_TOKEN: createAsyncTypes(`${KEY}/KIOSK_LOGIN_TOKEN`),
  RESET_KIOSK: `${KEY}/RESET_KIOSK`,
  RESET_ACTION: `${KEY}/RESET_ACTION`,
  RESET_ERROR: `${KEY}/RESET_ERROR`,
  RESET_MESSAGE: `${KEY}/RESET_MESSAGE`,
  RESET_KIOSK_LOGIN_TOKEN: `${KEY}/RESET_KIOSK_LOGIN_TOKEN`,
  RESET_EXPORT_DATA: `${KEY}/RESET_EXPORT_DATA`,
  UPDATE_AUTO_REFRESH: `${KEY}/UPDATE_AUTO_REFRESH`,
  UPDATE_SHOW_HIDDEN_KIOSKS: `${KEY}/UPDATE_SHOW_HIDDEN_KIOSKS`
};

// TODO: import Area and Venue?
export interface Venue {
  address: string;
  clientId: string;
  createdAt: string;
  id: string;
  name: string;
  updatedAt: string;
}

export interface Area {
  createdAt: string;
  description: string | null;
  updatedAt: string;
  id: string;
  name: string;
  venueId: string;
}

// as per new structure this needs to be update
export interface Kiosk {
  id: string;
  areaId: string;
  configurationId: string;
  createdAt: string;
  setupComplete?: boolean;
  schedule?: KioskSchedule;
  ipAddress: string | null;
  macAddress: string;
  name: string;
  status: string;
  updatedAt: string;
  venue: Venue;
  hardware: KioskHardware[];
  online?: boolean;
  area?: Area;
  venueName?: string;
  unscheduledUntil?: Date;
  lastPrint?: string | null;
  configuration?: KioskConfiguration;
  clientIntegrationId: string;
  clientIntegrations?: ClientIntegration;
  configurations?: Configuration;
  serialNumber: string | null;
}

export interface KioskConfiguration {
  disableHardwarePing?: string[];
  kioskPreview?: {
    enabled?: boolean;
    allowCheckOut?: boolean;
  };
  showInKCC?: boolean;
}

export type KioskSchedule<T = number> = KioskScheduleEntry<T>[];

export type DayNumber = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface KioskScheduleEntry<T = number> {
  id: string;
  kioskId: string;
  day: DayNumber;
  startTime: T;
  endTime: T;
}

export interface KiosksState {
  readonly kiosks: Kiosk[];
  readonly kiosk: Kiosk | null;
  readonly kioskSchedule: KioskSchedule;
  readonly loginToken: Record<string, string> | null;
  readonly version: string | null;
  readonly autoRefresh: boolean;
  readonly showHiddenKiosks: boolean;
  readonly action: {
    readonly pending: boolean;
    readonly success: boolean;
    readonly error: string | null;
    readonly message: string;
  };
  readonly export: {
    readonly data: Kiosk[];
    readonly pending: boolean;
  };
}

export interface CreateKioskPayload {
  name: string;
  areaId: string;
  configurationId: string;
  clientIntegrationId: string;
  macAddress?: string | null;
  hardware?: KioskHardware[];
  configuration?: {
    disableHardwarePing?: string[];
    kioskPreview?: {
      enabled?: boolean;
      allowCheckOut?: boolean;
    };
  };
}

export interface UpdateKioskSchedulePayload {
  kioskId: string;
  schedule: {
    day: DayNumber;
    startTime: number | undefined;
    endTime: number | undefined;
  }[];
}

export interface GetKiosksRequest {
  type: typeof types.GET_KIOSKS.REQUEST;
  payload: {
    exportData?: boolean;
  };
}

export interface GetKiosksPending {
  type: typeof types.GET_KIOSKS.PENDING;
  payload: {
    pending: boolean;
    exportData: boolean;
  };
}

export interface GetKiosksSuccess {
  type: typeof types.GET_KIOSKS.SUCCESS;
  payload: {
    kiosks: Kiosk[];
    exportData: boolean;
  };
}

export interface GetKiosksError {
  type: typeof types.GET_KIOSKS.ERROR;
  payload: string;
}

export interface GetKioskRequest {
  type: typeof types.GET_KIOSK.REQUEST;
  payload: {
    kioskId: string;
  };
}

export interface GetKioskPending {
  type: typeof types.GET_KIOSK.PENDING;
  payload: boolean;
}

export interface GetKioskSuccess {
  type: typeof types.GET_KIOSK.SUCCESS;
  payload: Kiosk;
}

export interface GetKioskError {
  type: typeof types.GET_KIOSK.ERROR;
  payload: string;
}

export interface CreateKioskRequest {
  type: typeof types.CREATE_KIOSK.REQUEST;
  payload: CreateKioskPayload;
}

export interface CreateKioskPending {
  type: typeof types.CREATE_KIOSK.PENDING;
  payload: boolean;
}

export interface CreateKioskSuccess {
  type: typeof types.CREATE_KIOSK.SUCCESS;
}

export interface CreateKioskError {
  type: typeof types.CREATE_KIOSK.ERROR;
  payload: string;
}

export interface CopyKioskPayload {
  id: string;
}

export interface CopyKioskRequest {
  type: typeof types.COPY_KIOSK.REQUEST;
  payload: CopyKioskPayload;
}

export interface CopyKioskPending {
  type: typeof types.COPY_KIOSK.PENDING;
  payload: boolean;
}

export interface CopyKioskSuccess {
  type: typeof types.COPY_KIOSK.SUCCESS;
}

export interface CopyKioskError {
  type: typeof types.COPY_KIOSK.ERROR;
  payload: string;
}

export type UpdateKioskPayload = Partial<Kiosk> & { id: string };

export interface UpdateKioskRequest {
  type: typeof types.UPDATE_KIOSK.REQUEST;
  payload: UpdateKioskPayload;
}

export interface UpdateKioskPending {
  type: typeof types.UPDATE_KIOSK.PENDING;
  payload: boolean;
}

export interface UpdateKioskSuccess {
  type: typeof types.UPDATE_KIOSK.SUCCESS;
  payload: Kiosk;
}

export interface UpdateKioskError {
  type: typeof types.UPDATE_KIOSK.ERROR;
  payload: string;
}

export interface UpdateKiosksRequest {
  type: typeof types.UPDATE_KIOSKS.REQUEST;
  payload: Partial<Kiosk>[];
}

export interface UpdateKiosksPending {
  type: typeof types.UPDATE_KIOSKS.PENDING;
  payload: boolean;
}

export interface UpdateKiosksSuccess {
  type: typeof types.UPDATE_KIOSKS.SUCCESS;
  payload: { kiosks: Kiosk[] };
}

export interface UpdateKiosksError {
  type: typeof types.UPDATE_KIOSKS.ERROR;
  payload: string;
}

export interface DeleteKioskRequest {
  type: typeof types.DELETE_KIOSK.REQUEST;
  payload: { id: string };
}

export interface DeleteKioskPending {
  type: typeof types.DELETE_KIOSK.PENDING;
  payload: boolean;
}

export interface DeleteKioskSuccess {
  type: typeof types.DELETE_KIOSK.SUCCESS;
  payload: { id: string };
}

export interface DeleteKioskError {
  type: typeof types.DELETE_KIOSK.ERROR;
  payload: string;
}

export interface GetKioskScheduleRequest {
  type: typeof types.GET_KIOSK_SCHEDULE.REQUEST;
  payload: {
    kioskId: string;
  };
}

export interface GetKioskSchedulePending {
  type: typeof types.GET_KIOSK_SCHEDULE.PENDING;
  payload: boolean;
}

export interface GetKioskScheduleSuccess {
  type: typeof types.GET_KIOSK_SCHEDULE.SUCCESS;
  payload: KioskSchedule;
}

export interface GetKioskScheduleError {
  type: typeof types.GET_KIOSK_SCHEDULE.ERROR;
  payload: string;
}

export interface UpdateKioskScheduleRequest {
  type: typeof types.UPDATE_KIOSK_SCHEDULE.REQUEST;
  payload: UpdateKioskSchedulePayload;
}

export interface UpdateKioskSchedulePending {
  type: typeof types.UPDATE_KIOSK_SCHEDULE.PENDING;
  payload: boolean;
}

export interface UpdateKioskScheduleSuccess {
  type: typeof types.UPDATE_KIOSK_SCHEDULE.SUCCESS;
  payload: KioskSchedule;
}

export interface UpdateKioskScheduleError {
  type: typeof types.UPDATE_KIOSK_SCHEDULE.ERROR;
  payload: string;
}

export interface RebootKioskRequest {
  type: typeof types.REBOOT_KIOSK.REQUEST;
  payload: { kioskId: string; adminId: string };
}

export interface RebootKioskPending {
  type: typeof types.REBOOT_KIOSK.PENDING;
  payload: boolean;
}

export interface RebootKioskSuccess {
  type: typeof types.REBOOT_KIOSK.SUCCESS;
}

export interface RebootKioskError {
  type: typeof types.REBOOT_KIOSK.ERROR;
  payload: string;
}

export interface ChangeStatusKioskRequest {
  type: typeof types.CHANGE_STATUS_KIOSK.REQUEST;
  payload: { kioskId: string; status: string; adminId: string };
}

export interface ChangeStatusKioskPending {
  type: typeof types.CHANGE_STATUS_KIOSK.PENDING;
  payload: boolean;
}

export interface ChangeStatusKioskSuccess {
  type: typeof types.CHANGE_STATUS_KIOSK.SUCCESS;
}

export interface ChangeStatusKioskError {
  type: typeof types.CHANGE_STATUS_KIOSK.ERROR;
  payload: string;
}

export interface CheckAppVersionRequest {
  type: typeof types.CHECK_APP_VERSION.REQUEST;
}

export interface CheckAppVersionPending {
  type: typeof types.CHECK_APP_VERSION.PENDING;
  payload: boolean;
}

export interface CheckAppVersionSuccess {
  type: typeof types.CHECK_APP_VERSION.SUCCESS;
  payload: string;
}

export interface LogEventRequest {
  type: typeof types.LOG_EVENT.REQUEST;
  payload: { kioskId: string; adminId: string };
}

export interface LogEventPending {
  type: typeof types.LOG_EVENT.PENDING;
  payload: boolean;
}

export interface LogEventSuccess {
  type: typeof types.LOG_EVENT.SUCCESS;
}

export interface LogEventError {
  type: typeof types.LOG_EVENT.ERROR;
  payload: string;
}

export interface ResetAction {
  type: typeof types.RESET_ACTION;
}

export interface ResetError {
  type: typeof types.RESET_ERROR;
}

export interface ResetMessage {
  type: typeof types.RESET_MESSAGE;
}

export interface KioskLoginTokenRequest {
  type: typeof types.KIOSK_LOGIN_TOKEN.REQUEST;
  payload: { kioskId: string; adminId: string };
}

export interface KioskLoginTokenPending {
  type: typeof types.KIOSK_LOGIN_TOKEN.PENDING;
  payload: boolean;
}

export interface KioskLoginTokenSuccess {
  type: typeof types.KIOSK_LOGIN_TOKEN.SUCCESS;
  payload: Record<string, string>;
}

export interface KioskLoginTokenError {
  type: typeof types.KIOSK_LOGIN_TOKEN.ERROR;
  payload: string;
}

export interface UpdateAutoRefresh {
  type: typeof types.UPDATE_AUTO_REFRESH;
  payload: boolean;
}

export interface UpdateShowHiddenKiosks {
  type: typeof types.UPDATE_SHOW_HIDDEN_KIOSKS;
  payload: boolean;
}

export type KiosksActionTypes =
  | GetKiosksRequest
  | GetKiosksPending
  | GetKiosksSuccess
  | GetKiosksError
  | GetKioskRequest
  | GetKioskPending
  | GetKioskSuccess
  | GetKioskError
  | CreateKioskRequest
  | CreateKioskPending
  | CreateKioskSuccess
  | CreateKioskError
  | CopyKioskRequest
  | CopyKioskPending
  | CopyKioskSuccess
  | CopyKioskError
  | UpdateKioskRequest
  | UpdateKioskPending
  | UpdateKioskSuccess
  | UpdateKioskError
  | UpdateKiosksRequest
  | DeleteKioskRequest
  | DeleteKioskPending
  | DeleteKioskSuccess
  | DeleteKioskError
  | GetKioskScheduleRequest
  | GetKioskSchedulePending
  | GetKioskScheduleSuccess
  | GetKioskScheduleError
  | UpdateKioskScheduleRequest
  | UpdateKioskSchedulePending
  | UpdateKioskScheduleSuccess
  | UpdateKioskScheduleError
  | RebootKioskRequest
  | RebootKioskPending
  | RebootKioskSuccess
  | RebootKioskError
  | ChangeStatusKioskRequest
  | ChangeStatusKioskPending
  | ChangeStatusKioskSuccess
  | ChangeStatusKioskError
  | CheckAppVersionRequest
  | CheckAppVersionPending
  | CheckAppVersionSuccess
  | LogEventRequest
  | LogEventPending
  | LogEventSuccess
  | LogEventError
  | ResetAction
  | ResetError
  | ResetMessage
  | KioskLoginTokenRequest
  | KioskLoginTokenPending
  | KioskLoginTokenSuccess
  | KioskLoginTokenError
  | UpdateAutoRefresh
  | UpdateShowHiddenKiosks;
